import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from "react";

/**
 * `<input type="file">` wrapper that swallows the native `cancel` event so
 * it never leaves the input. When the OS file picker is dismissed (ESC or
 * the picker's own Cancel button), the browser fires a bubbling `cancel`
 * event on the input. React 19 does not bind an onCancel handler on
 * `<input>` — only on `<dialog>` — so that event bubbles up and, in some
 * browsers, triggers the default "close" action of the nearest open
 * `<dialog>`, closing the surrounding modal/editor and losing user work.
 *
 * Attaching a native listener directly to the input lets us stop the
 * propagation at its source, which is safer than chasing it with capture
 * listeners on every ancestor dialog.
 *
 * Drop-in replacement for `<input type="file" …>`. All props forward to
 * the underlying input.
 */
type FileInputSafeProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

export const FileInputSafe = forwardRef<HTMLInputElement, FileInputSafeProps>(
  function FileInputSafe(props, forwardedRef) {
    const innerRef = useRef<HTMLInputElement>(null);
    const setRef = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) (forwardedRef as { current: HTMLInputElement | null }).current = node;
    };

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const stop = (e: Event) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      el.addEventListener("cancel", stop);
      return () => el.removeEventListener("cancel", stop);
    }, []);

    return <input {...props} type="file" ref={setRef} />;
  },
);
